import {Helmet} from '@dr.pogodin/react-helmet';
import {openConfirmDialog, openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteTireCategoryMutation,
  usePostTireCategorySetActiveMutation,
  usePostTireCategorySetDefaultMutation,
  usePostTireCategorySetInactiveMutation,
  usePostTireCategoryUnsetDefaultMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function TireInventoryCategory() {
  const navigate = useNavigate();

  const [deleteTireCategoryApi] = useDeleteTireCategoryMutation();
  const [postTireCategorySetActiveApi] = usePostTireCategorySetActiveMutation();
  const [postTireCategorySetInactiveApi] = usePostTireCategorySetInactiveMutation();
  const [postTireCategorySetDefaultApi] = usePostTireCategorySetDefaultMutation();
  const [postTireCategoryUnsetDefaultApi] = usePostTireCategoryUnsetDefaultMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        navigate(composePath(settingsRoutes.tireCategoryDetail, {params: {id}}))
      )
      .with('setAsInactive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireCategorySetInactiveApi({tireCategoryId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setAsActive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireCategorySetActiveApi({tireCategoryId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setAsDefault', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireCategorySetDefaultApi({tireCategoryId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteTireCategoryApi({tireCategoryId: id})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('unsetAsDefault', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireCategoryUnsetDefaultApi({tireCategoryId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('entity.tiresInventory.labels.tireCategory')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.tiresInventory.labels.tireCategory'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.tiresInventory.labels.newTireCategory'),
              onClick: () => navigate(settingsRoutes.tireCategoryCreate),
            },
          ],
        }}
        data-testid={testIds.settings.tireCategory('template')}
      >
        <DmsDataGrid
          gridCode="tire-category"
          actionCallback={actionCallback}
          data-testid={testIds.settings.tireCategory('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
